import { useEffect, useMemo, useState } from "react";
import {
  renderDisplayTemplate,
  validateTemplate,
  type TemplateValidationIssue,
} from "@capture/shared";
import { api } from "@/lib/api-client";
import { showApiError } from "@/lib/error-toast";
import { useCurrentTenant } from "@/hooks/useCurrentTenant";
import { useCustomCounters } from "../counters/hooks/useCustomCounters";
import TokenCatalogPanel from "../jobtype/components/TokenCatalogPanel";
import styles from "./tenant-settings.module.css";

/**
 * R-146 — admin UI for the per-tenant display-name templates.
 *
 * Renders three text inputs (Batch / Document / Page) with a live preview
 * underneath each one. Saving PATCHes /api/me/tenant; clearing a field
 * (empty input) stores null and the surface reverts to the built-in
 * structured `displayId`.
 *
 * R-167 — extended with the shared `TokenCatalogPanel` cheatsheet and
 * `validateTemplate` chips for parity with the per-JobType section.
 * Tenant-level context: all configured counters (job-type-scoped AND
 * tenant-scoped) are valid; field tokens are NOT validated here because
 * field-internal-names are job-type-scoped (the per-JobType section
 * handles those).
 *
 * Sample records used for live preview are intentionally hard-coded so
 * tenants can see the effect without needing real data on screen.
 */

const SAMPLE_BATCH = {
  displayId: "NOW_20260523_0042",
  jobType: { shortName: "NOW" },
  createdAt: new Date(Date.UTC(2026, 4, 23, 0, 0, 0)),
  seq: 42,
  // R-167 sample data so the preview reflects the full token set when
  // a tenant pastes in a new-style template.
  counterValues: { batchSeq: 2003 },
  counterPadding: { batchSeq: 4 },
  sourceCode: "SCN",
  docPosition: 1,
  pagePosition: 1,
};
const SAMPLE_DOC = {
  ...SAMPLE_BATCH,
  displayId: "NOW_20260523_0042_01",
  seq: 1,
  docPosition: 1,
};
const SAMPLE_PAGE = {
  ...SAMPLE_BATCH,
  displayId: "NOW_20260523_0042_01_001",
  seq: 1,
  pagePosition: 1,
};

type SlotKey = "batch" | "doc" | "page";

const TOKEN_HELP =
  "Tokens: {displayId}, {shortName}, {YYYY}, {MM}, {DD}, {seq}, {counter:NAME}, {sourceCode}, …. " +
  "Click Tokens for the full cheatsheet. Leave blank to use the built-in display id.";

const tokensBtnStyle: React.CSSProperties = {
  padding: "var(--space-2, 0.5rem) var(--space-3, 0.75rem)",
  background: "transparent",
  color: "#ea580c",
  border: "1px solid #ea580c",
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  marginLeft: "auto",
};

const issueChipStyle: React.CSSProperties = {
  display: "inline-block",
  background: "rgba(220, 38, 38, 0.1)",
  border: "1px solid #dc2626",
  color: "#dc2626",
  borderRadius: 4,
  padding: "2px 6px",
  margin: "4px 4px 0 0",
  fontSize: 12,
};

export function DisplayTemplatesSection() {
  const { data: tenant, loading } = useCurrentTenant();
  const { counters } = useCustomCounters();
  const [batch, setBatch] = useState("");
  const [doc, setDoc] = useState("");
  const [page, setPage] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [focused, setFocused] = useState<SlotKey | null>(null);

  // R-167 — every counter the tenant has configured is fair game at
  // the tenant level. Field validation is intentionally permissive
  // because fields are job-type-scoped (the per-JobType section
  // handles field-internal-name validation against its own fields).
  const ctx = useMemo(
    () => ({
      knownCounterNames: new Set((counters ?? []).map((c) => c.name)),
      knownFieldInternalNames: new Set<string>(),
    }),
    [counters],
  );

  // Seed local form state from the fetched tenant once it loads.
  useEffect(() => {
    if (!tenant) return;
    setBatch(tenant.batchDisplayTemplate ?? "");
    setDoc(tenant.documentDisplayTemplate ?? "");
    setPage(tenant.pageDisplayTemplate ?? "");
  }, [tenant]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.patch("/me/tenant", {
        batchDisplayTemplate: batch.trim() === "" ? null : batch.trim(),
        documentDisplayTemplate: doc.trim() === "" ? null : doc.trim(),
        pageDisplayTemplate: page.trim() === "" ? null : page.trim(),
      });
      setMessage({ kind: "ok", text: "Display templates saved." });
    } catch (e) {
      showApiError(e, { fallback: "Failed to save display templates" });
      setMessage({ kind: "err", text: "Save failed — see toast for details." });
    } finally {
      setSaving(false);
    }
  };

  function insertToken(token: string) {
    if (!focused) return;
    if (focused === "batch") setBatch((v) => v + token);
    else if (focused === "doc") setDoc((v) => v + token);
    else if (focused === "page") setPage((v) => v + token);
  }

  function issuesFor(template: string): TemplateValidationIssue[] {
    return template.trim() === "" ? [] : validateTemplate(template, ctx);
  }

  const batchIssues = issuesFor(batch);
  const docIssues = issuesFor(doc);
  const pageIssues = issuesFor(page);

  return (
    <section className={styles.section}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <h2 className={styles.sectionHeading} style={{ marginBottom: 0, flexGrow: 1 }}>
          Display Names
        </h2>
        <button
          type="button"
          onClick={() => setCheatsheetOpen(true)}
          style={tokensBtnStyle}
          data-testid="display-templates-tokens-button"
        >
          Tokens…
        </button>
      </div>
      <p className={styles.overviewHint} style={{ marginTop: "var(--space-3, 0.75rem)" }}>
        Customize how batch, document, and page labels appear throughout the
        app. The underlying display id is unchanged — only the operator-visible
        label is reformatted. {TOKEN_HELP}
      </p>

      <div className={styles.formGrid} style={{ marginTop: "var(--space-4, 1rem)" }}>
        <label className={styles.fieldLabel} htmlFor="dt-batch">Batch label</label>
        <div>
          <input
            id="dt-batch"
            className={styles.textInput}
            type="text"
            value={batch}
            onChange={(e) => setBatch(e.target.value)}
            onFocus={() => setFocused("batch")}
            placeholder="{displayId}"
            data-testid="display-template-batch"
            disabled={loading}
          />
          <div className={styles.overviewHint} data-testid="display-template-batch-preview">
            Preview:{" "}
            <strong>
              {renderDisplayTemplate(batch.trim() === "" ? null : batch, SAMPLE_BATCH)}
            </strong>
          </div>
          {batchIssues.length > 0 && (
            <div data-testid="display-template-batch-issues">
              {batchIssues.map((i, idx) => (
                <span key={idx} style={issueChipStyle}>
                  <strong>{i.kind}</strong>: {i.message}
                </span>
              ))}
            </div>
          )}
        </div>

        <label className={styles.fieldLabel} htmlFor="dt-doc">Document label</label>
        <div>
          <input
            id="dt-doc"
            className={styles.textInput}
            type="text"
            value={doc}
            onChange={(e) => setDoc(e.target.value)}
            onFocus={() => setFocused("doc")}
            placeholder="{displayId}"
            data-testid="display-template-doc"
            disabled={loading}
          />
          <div className={styles.overviewHint} data-testid="display-template-doc-preview">
            Preview:{" "}
            <strong>
              {renderDisplayTemplate(doc.trim() === "" ? null : doc, SAMPLE_DOC)}
            </strong>
          </div>
          {docIssues.length > 0 && (
            <div data-testid="display-template-doc-issues">
              {docIssues.map((i, idx) => (
                <span key={idx} style={issueChipStyle}>
                  <strong>{i.kind}</strong>: {i.message}
                </span>
              ))}
            </div>
          )}
        </div>

        <label className={styles.fieldLabel} htmlFor="dt-page">Page label</label>
        <div>
          <input
            id="dt-page"
            className={styles.textInput}
            type="text"
            value={page}
            onChange={(e) => setPage(e.target.value)}
            onFocus={() => setFocused("page")}
            placeholder="{displayId}"
            data-testid="display-template-page"
            disabled={loading}
          />
          <div className={styles.overviewHint} data-testid="display-template-page-preview">
            Preview:{" "}
            <strong>
              {renderDisplayTemplate(page.trim() === "" ? null : page, SAMPLE_PAGE)}
            </strong>
          </div>
          {pageIssues.length > 0 && (
            <div data-testid="display-template-page-issues">
              {pageIssues.map((i, idx) => (
                <span key={idx} style={issueChipStyle}>
                  <strong>{i.kind}</strong>: {i.message}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.actionRow} style={{ marginTop: "var(--space-4, 1rem)" }}>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleSave}
          disabled={saving || loading}
          data-testid="display-templates-save"
        >
          {saving ? "Saving…" : "Save Display Names"}
        </button>
        {message && (
          <span
            className={message.kind === "ok" ? styles.testOk : styles.testErr}
            data-testid="display-templates-status"
          >
            {message.text}
          </span>
        )}
      </div>

      {cheatsheetOpen && (
        <TokenCatalogPanel
          onInsert={insertToken}
          onClose={() => setCheatsheetOpen(false)}
        />
      )}
    </section>
  );
}
